
import { useState } from 'react';
import { Interview } from '@/types/interview';
import { toast } from 'sonner';
import { InterviewService } from '@/services/interview/interviewService';
import { useQueryClient } from '@tanstack/react-query';

export const useInterviewActions = (
  handleCloseViewDialog: () => void,
  handleCloseEditDialog: () => void,
  onPublish: (interviewId: string) => Promise<void>
) => {
  const queryClient = useQueryClient();
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [interviewToProcess, setInterviewToProcess] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Confirm before publishing
  const confirmPublish = (interviewId: string) => {
    setInterviewToProcess(interviewId);
    setIsPublishConfirmOpen(true);
  };
  
  // Confirm before deleting
  const confirmDelete = (interviewId: string) => {
    setInterviewToProcess(interviewId);
    setIsDeleteConfirmOpen(true);
  };

  // Execute actual publish after confirmation
  const handleConfirmedPublish = async () => {
    if (interviewToProcess) {
      await onPublish(interviewToProcess);
      setInterviewToProcess(null);
      setIsPublishConfirmOpen(false);
    }
  };
  
  const handleConfirmedDelete = async () => {
    if (interviewToProcess) {
      try {
        // Close the dialog immediately to give visual feedback
        setIsDeleteConfirmOpen(false);
        
        // Perform the delete operation
        await handleDelete(interviewToProcess);
      } finally {
        // Always clear the interview being processed
        setInterviewToProcess(null);
      }
    }
  };
  
  // Execute actual delete
  const handleDelete = async (interviewId: string) => {
    if (deletingId) {
      console.log('Already deleting another interview, skipping');
      return;
    }
    
    try {
      console.log('FeederInterviewsList - Deleting interview:', interviewId);
      
      // Set the deleting state immediately to prevent multiple clicks
      setDeletingId(interviewId);
      
      // Show deletion toast
      toast.loading('Sletter intervju...', { id: 'deleting-interview' });
      
      // Call the service to delete the interview
      const result = await InterviewService.deleteInterview(interviewId);
      
      if (result) {
        console.log('Delete successful, invalidating and refreshing queries');
        
        // First close any dialogs that might be open
        handleCloseViewDialog();
        handleCloseEditDialog();
        
        // Invalidate all queries that might contain this interview
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['pendingInterviews'] }),
          queryClient.invalidateQueries({ queryKey: ['feederInterviews'] }),
          queryClient.invalidateQueries({ queryKey: ['interviews'] })
        ]);
        
        toast.success('Intervju slettet', { id: 'deleting-interview' });
        
        // Force refresh all queries
        await queryClient.refetchQueries();
      } else {
        toast.error('Kunne ikke slette intervju', { id: 'deleting-interview' });
        throw new Error('Kunne ikke slette intervju');
      }
    } catch (error) {
      console.error('Error deleting interview:', error);
      toast.error(`Kunne ikke slette intervju: ${error instanceof Error ? error.message : 'Ukjent feil'}`, { id: 'deleting-interview' });
    } finally {
      // Always clear the deleting state
      setDeletingId(null);
    }
  };

  return {
    isPublishConfirmOpen,
    isDeleteConfirmOpen,
    setIsPublishConfirmOpen,
    setIsDeleteConfirmOpen,
    interviewToProcess,
    deletingId,
    confirmPublish,
    confirmDelete,
    handleConfirmedPublish,
    handleConfirmedDelete,
    handleDelete
  };
};
